import React, { ComponentType, useCallback, useMemo } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faIndustry, faShoppingBasket, faStar, faTruck
} from '@fortawesome/free-solid-svg-icons';
import Allergens from '../Allergens';
import ArticleBadges from '../ArticleBadges';
import ArticleImage from '../ArticleImage';
import ArticleOrderHistory from '../ArticleOrderHistory';
import ArticlePromo, { ArticlePromoProps } from '../ArticlePromo';
import Highlight from '../Highlight';
import { ProjectArticle } from '../Article/ProjectArticle';
import { ConditionalWrapper } from '../ConditionalWrapper';
import { CommonQuantityInput, CommonQuantityInputProps } from '../QuantityInput/CommonQuantityInput';
import { TempNotAvailable } from '../TempNotAvailable';
import { TranslateFunction } from '../../types';
import Badge from '../Badge';
import './OrderArticle.styles.scss';
import Coin from '../Coin';

export enum VIEWS {
	GRID = 'grid',
	COMPACT = 'list-compact',
	LIST = 'list',
}

export const getDateFromReversedDateString = (dateString: string) => {
	if (!dateString) return null;
	const year = Number(dateString.substr(0, 4));
	const month = Number(dateString.substr(5, 2));
	const day = Number(dateString.substr(8, 2));

	return new Date(year, month - 1, day, 0, 0, 0, 0);
};

export const getWeekNumber = (d: Date) => {
	if (!d) return undefined;
	const newDate = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
	newDate.setUTCDate(newDate.getUTCDate() + 4 - (newDate.getUTCDay() || 7));
	const yearStart = new Date(Date.UTC(newDate.getUTCFullYear(), 0, 1));
	const weekNo = Math.ceil((((newDate.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
	return [d.getUTCFullYear(), weekNo];
};

const getPendingForDeliveryWeek = (pending?: boolean, deliveryDate?: Date) => {
	if (!pending) return 0;
	if (!deliveryDate) deliveryDate = new Date();
	const pendingByWeek = {};
	Object.keys(pending).forEach(datestamp => {
		const date = getDateFromReversedDateString(datestamp);
		const week = getWeekNumber(date)[1];
		pendingByWeek[week] = pending[datestamp] + (pendingByWeek[week] || 0);
	});

	const deliveryWeek = getWeekNumber(new Date(deliveryDate))[1];
	return pendingByWeek[deliveryWeek] || 0;
};

export const getHeight = (view: `${VIEWS}`, size: string, article: any) => {
	switch (view) {
		case 'grid':
			return 550;

		case 'list-compact':
			// if no promos and no reserved or pending and no quantity or awardedPromo -> return 56
			if (article.promo) return 80;
			if (article.pending && getPendingForDeliveryWeek(article.pending, article.deliveryDate) > 0) return 80;
			if (article.reserved) return 80;
			if (article.campaigns && article.campaigns.length > 0) return 80;
			return 52;

		case 'list':
		default:
			const sizes = {
				size935: 154,
				size770: 132,
				size720: 132,
				size630: 132
			};
			const maximumHeight = 168;
			return sizes[size] ?? maximumHeight;
	}
};

const getDerivedClasses = ({
	hasPromo, isPending, hasReserved, hasCampaigns, view
}) => {
	if (view !== 'list-compact' || hasPromo || isPending || hasReserved || hasCampaigns) {
		return '';
	}
	return 'without-bottom';
};

const ASSOONASPOSSIBLEDELIVERYDATE = '01-01-2500';

type ALLERGENS = 1 | 2 | 3 | 4 | 5 | 6 | '7.1' | '7.2' | 8 | 9 | 10 | 11 | 12 | 13 | 14;

interface Product {
	id: string;
	code: string;
	brand: string;
	canOrder: boolean;
	name: string;
	promo: any;
	eob: any;
	season: any;
	start: any;
	end: any;
	inBuylist: boolean;
	canRemoveFromBuylist: boolean;
	canAddToBuylist: boolean;
	campaigns: any[];
	madQuantity: number;
	images: {
		PS: string[];
		SF: string[];
	};
	allergens: ALLERGENS[];
	reserved?: any;
	pending?: boolean;
	orderAmount?: any;
	orderedQuantity?: number;
	freeQuantity?: number;
	points?: {
		normal?: number;
		tradeFair?: number;
	}
}

interface OrderArticleProps extends Pick<ArticlePromoProps, 'hideRibbon' | 'disabledPromo' | 'setDisabledPromo'> {
	view?: `${VIEWS}`;
	awardedPromo?: any;
	isFetching?: boolean;
	sizes: any;
	showModal: (product: any) => void;
	updateProductQuantity: () => void;
	product?: Product;
	query?: string;
	hideQuantityInput?: boolean;
	locale: string;
	t: TranslateFunction;
	tempNotAvailable?: boolean;
	backAvailableFrom?: string;
	eobDetail: any;
	imageSource: string;
	quantity: number;
	deliveryDate: Date;
	removeProductFromBuylist: () => void,
	addProductToBuylist: () => void;
	isInBuylist?: boolean;
	// history: PropTypes.objectOf(PropTypes.number),
	history: any;
	isHistoryFetching?: boolean;
	showMadQuantityWarning?: boolean;
	QuantityInput?: ComponentType<CommonQuantityInputProps>;
	maxWeeks?: any;
	quantityTimestamp?: any;
	points?: {
		normal?: number;
		tradeFair?: number;
	};
}

// TODO: Replace hardcorded color values
export const OrderArticle = (props: OrderArticleProps) => {
	const {
		view, awardedPromo, history, sizes, showModal, updateProductQuantity, product, query, hideQuantityInput, locale, t, tempNotAvailable, backAvailableFrom, eobDetail, imageSource, quantity, deliveryDate, isHistoryFetching, QuantityInput, maxWeeks, hideRibbon, quantityTimestamp, showMadQuantityWarning, disabledPromo, setDisabledPromo
	} = props;

	// TODO: conditional based on the view/format
	const isTiny = sizes?.includes('size630');
	const isSmall = sizes?.includes('size770');
	const isNormal = sizes?.includes('size935');

	const openModal = useCallback(() => showModal(product), [product, showModal]);

	const totalReserved = useMemo(() => {
		if (!product?.reserved) {
			return undefined;
		}

		const total = Object.values(product.reserved).reduce<number>((acc, reservedAtDate: number) => acc + reservedAtDate, 0);

		if (!total) {
			return undefined;
		}

		return Number(total);
	}, [product?.reserved]);

	const eobDetailsText = useMemo(() => {
		if (!eobDetail) {
			return '';
		}

		const text = eobDetail.map((eob) => {
			return `${t('order.productReservedEobDetails', eob.deliveryDate !== ASSOONASPOSSIBLEDELIVERYDATE ? eob.deliveryDate : t('order.asSoonAsPossible'), eob.amount)}`;
		}).join('\n');

		return text;
	}, [eobDetail, t]);

	const pendingThisWeek = useMemo(() => {
		return getPendingForDeliveryWeek(product?.pending, deliveryDate);
	}, [product?.pending, deliveryDate]);

	const images = useMemo(() => {
		const _images = [];

		if (!product?.images) {
			return _images;
		}

		['PS', 'SF'].forEach(type => {
			if (product?.images[type]?.length) {
				_images.push({
					size: 400,
					file: product.images[type][0]
				});
			}
		});

		return _images;
	}, [product?.images]);

	const QuantityInputComponent = QuantityInput || CommonQuantityInput;

	const isPending = useMemo(() => {
		return product.pending && getPendingForDeliveryWeek(product.pending, deliveryDate) > 0;
	}, [product?.pending, deliveryDate]);


	return (
		<ProjectArticle
			className={`Article OrderArticle ${view} ${getDerivedClasses({
				view, hasPromo: !!product.promo, isPending, hasReserved: product?.reserved, hasCampaigns: !!product?.campaigns?.length
			})} ${sizes?.join(' ')}`}
			key={`art-list-${product.id}`}
		>
			{view !== 'list-compact' && <ArticleImage location={imageSource} alt={product.name} className={'Article__image'} onClick={openModal}>
				{Source => images.map(source =>
					<Source key={JSON.stringify(source)} file={source.file} size={source.size} />
				)}
			</ArticleImage>}
			{view === 'grid' && <div className='flexBoxCol Article__in-order-indicator '>
				{(quantity || awardedPromo?.quantity) && <Badge
					secondary={awardedPromo ? t('order.productPromoAwarded') : undefined}
					className={'Article__in-order-indicator reverse'}
					size={isSmall ? '' : 'big'}
					color={{
						background: '#187D53',
						secondary: '#1c9462',
					}}
				>
					<FontAwesomeIcon icon={faShoppingBasket} />
				</Badge>
				}
				<div style={{ display: "flex", gap: "0.5rem", marginTop: "0.2rem", flexWrap: "wrap", justifyContent: "flex-end" }}>
					<Coin size="small" points={[props.points?.normal]} />
					<Coin size="small" koerse points={[props.points?.tradeFair]} />
				</div>
			</div>
			}

			<div className={'detail'}>
				<div className={'top'}>
					<Highlight query={query} className={'Article__id'}>{product.code}</Highlight>
					<Highlight query={query} className={'Article__brand'}>{product.brand}</Highlight>
					<ArticleBadges
						t={t}
						size={isTiny ? 'small' : ''}
						eob={product.eob}
						season={product.season}
						start={product.start}
						end={product.end}
						className={'Article__badges'}
						{...props}
					/>

				</div>

				<div className={'middle'}>
					{product.inBuylist && <FontAwesomeIcon icon={faStar} className={'Article__buylistIndicator'} />}
					<Highlight query={query} className={'Article__name'} onClick={openModal}>{product.name}</Highlight>
				</div>

				<Allergens
					{...props}
					t={t}
					allergens={product.allergens}
					compact={isNormal}
					view={isSmall ? 'grid' : ''}
				/>

				{view !== 'list-compact' && <ArticleOrderHistory
					size={view === 'grid' ? '' : 'compact'}
					history={history}
					isFetching={isHistoryFetching}
					t={t}
					maxWeeks={maxWeeks}
					className='Article__history'
				/>}
			</div>

			<ConditionalWrapper condition={view === 'list'}>
				<div className={'order'}>
					{view === 'list-compact' && <ArticleOrderHistory
						size='compact'
						history={history}
						isFetching={isHistoryFetching}
						t={t}
						maxWeeks={maxWeeks}
						className='Article__history'
					/>}

					{!hideQuantityInput &&
						<QuantityInputComponent
							size={(view === 'list-compact' || isSmall) ? 'compact' : ''}
							t={t}
							quantity={quantity}
							quantityTimestamp={quantityTimestamp}
							awardedPromo={awardedPromo?.quantity}
							onQuantityChange={product.canOrder ? updateProductQuantity : null}
							disabled={!product.canOrder}
							orderAmount={product.orderAmount}
							madQuantityIndicator={showMadQuantityWarning}
							debounce={300}
						/>
					}

					{(hideQuantityInput && tempNotAvailable) && <TempNotAvailable backAvailableFrom={backAvailableFrom} compact={view === 'list-compact'} t={t} />}
					{view !== 'grid' && <div className='flexBoxRow'>
						<Coin size={view === "list-compact" ? "small" : "medium"} points={[props.points?.normal]} />
						<Coin size={view === "list-compact" ? "small" : "medium"} koerse points={[props.points?.tradeFair]} />
						{(quantity || awardedPromo?.quantity) && <Badge
							secondary={awardedPromo && t('order.productPromoAwarded')}
							className={'Article__in-order-indicator reverse'}
							size={isSmall ? '' : 'big'}
							color={{
								background: '#187D53',
								secondary: '#1c9462',
							}}
						>
							<FontAwesomeIcon icon={faShoppingBasket} />
						</Badge>}
					</div>}

					{view === 'grid' && <div>
						{!!pendingThisWeek && <Badge
							hint={t('order.productPendingLabel') as string}
							secondary={pendingThisWeek}
							className={'Article__pending'}
							color='#3885AE'
							size='big'
						>
							<FontAwesomeIcon icon={faTruck} />
						</Badge>}

						{/* TODO: has issues on list */}
						{!!totalReserved && <Badge
							hint={t('order.productReservedLabel') + (eobDetail ? '\n\n' : '') + eobDetailsText}
							secondary={totalReserved}
							className={'Article__reserved'}
							color='#3885AE'
							size='big'
							eobDetails={eobDetail}
							t={t}
						>
							<FontAwesomeIcon icon={faIndustry} />
						</Badge>}
					</div>}
				</div>

				<div className={'bottom'}>
					<div>
						{!!product?.campaigns?.length &&
							<div className={'Article__campaigns'}>
								{product.campaigns.map(campaign => {
									if (!campaign?.articles?.badge) {
										return null;
									}

									return (
										<Badge
											key={campaign.id}
											className={'Article__campaigns__item'}
											color={{
												text: campaign?.articles?.badge.color.text,
												background: campaign?.articles?.badge.color.background,
											}}
											size={view === 'list-compact' || isNormal ? 'small' : ''}
										>
											{campaign?.articles?.badge?.text[locale]}
										</Badge>
									);
								})}
							</div>
						}

						{product.promo && <ArticlePromo
							deliveryDate={deliveryDate}
							promo={product.promo}
							t={t}
							productId={product.id}
							hideRibbon={view === 'list-compact' || hideRibbon}
							disabledPromo={disabledPromo}
							setDisabledPromo={setDisabledPromo}
						/>}
					</div>

					{view !== 'grid' && <div>
						{!!pendingThisWeek && <Badge
							hint={t('order.productPendingLabel') as string}
							secondary={pendingThisWeek}
							className={'Article__pending'}
							color='#3885AE'
							size={(view === 'list-compact' || isSmall) ? 'small' : ''}
						>
							<FontAwesomeIcon icon={faTruck} />
						</Badge>}

						{/* TODO: has issues on list-compact */}
						{!!totalReserved && <Badge
							hint={t('order.productReservedLabel') + (eobDetail ? '\n\n' : '') + eobDetailsText}
							secondary={totalReserved}
							className={'Article__reserved'}
							color='#3885AE'
							size={(view === 'list-compact' || isSmall) ? 'small' : ''}
							eobDetails={eobDetail}
							t={t}
						>
							<FontAwesomeIcon icon={faIndustry} />
						</Badge>}
					</div>}

					{(view === 'list') && <div className={'bottom__right'}>
						{!!(product?.orderedQuantity && product?.freeQuantity) && (
							<div className='ordered'>
								{t('expo.orderedHere', product.orderedQuantity, product.freeQuantity)}
							</div>
						)}

						{(!!product?.orderedQuantity && !product?.freeQuantity) && (
							<div className='ordered'>
								{t('expo.orderedHereNoFree', product.orderedQuantity)}
							</div>
						)}
					</div>}
				</div>
			</ConditionalWrapper>
		</ProjectArticle>
	);
};
